import chunk from "lodash.chunk";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FinancialBreakdownController } from "services/financialBreakdown/controller";
import { AccordionCard, Button, useAccordionWizard } from "stories";
import { useFetchState } from "utils/customHooks/useFetchState";
import { useToast } from "utils/customHooks/useToast";
import { AssetBreakdownSection } from "./elements/Section";
import { SummerySection } from "./elements/Summery";
import { getEmptyRow } from "./elements/Table";

const deserializeAssets = (assets) => {
  let result = {};
  for (const fullKey of Object.keys(assets)) {
    const keySplit = fullKey.split(">");
    let p = result;
    for (const key of keySplit) {
      if (!p[key]) {
        p[key] = {};
      }
      if (key !== keySplit[keySplit.length - 1]) p = p[key];
    }
    p[keySplit[keySplit.length - 1]] = assets[fullKey].map((row) => ({
      avg_price: Number(parseFloat(row.avg_price)),
      avg_sq_ft: Number(parseFloat(row.avg_sq_ft)),
      count: Number(parseFloat(row.count)),
      id: row.id,
      title: row.title,
    }));
  }
  return result;
};

const processData = (data, prefixKey) => {
  let processedData = {};
  if (Array.isArray(data)) {
    return {
      [prefixKey]:
        data.length === 0
          ? [getEmptyRow()]
          : data.map((d) => ({ ...getEmptyRow(), ...d })),
    };
  }

  for (const objKey of Object.keys(data)) {
    processedData = {
      ...processedData,
      ...processData(
        data[objKey],
        `${prefixKey ? prefixKey + ">" : ""}${objKey}`
      ),
    };
  }
  return processedData;
};

const rowValidation = (rows) => {
  if (rows.length === 0) {
    return "At least one row should be added.";
  } else if (rows.length > 0) {
    let atLeastOneRowFilled = true;
    for (const row of rows) {
      if (
        typeof row.avg_price === "undefined" ||
        typeof row.avg_sq_ft === "undefined" ||
        typeof row.count === "undefined" ||
        typeof row.title === "undefined" ||
        row.avg_price === null ||
        row.avg_sq_ft === null ||
        row.count === null ||
        row.title === null
      ) {
        atLeastOneRowFilled = false;
      }
    }
    if (atLeastOneRowFilled === false) {
      return "Please fill all of the fields";
    }
  }
};

const assetsValidation = (assets) => {
  const errorKeys = {};
  for (const key of Object.keys(assets)) {
    const error = rowValidation(assets[key]);
    if (error) errorKeys[key] = error;
  }
  return errorKeys;
};

const getDefaultFormValue = () => {
  return {
    project_purpose: null,
    assets: {},
    parking_assets: [],
    storage_assets: [],
    sale_tax_percentage: null,
  };
};

const PSProcessData = (data) => {
  return !!!data || (!!data && data.length === 0)
    ? [getEmptyRow()]
    : data.map((e) => ({
        ...getEmptyRow(),
        ...e,
      }));
};

const pSValidation = (key, data, hasPS) => {
  if (hasPS) {
    const error = rowValidation(data);
    if (error) {
      return {
        [key]: error,
      };
    }
  }
};

const parsePSdata = (rows) =>
  rows
    .filter((row) => row.avg_price)
    .map((row) => ({
      avg_price: parseFloat(row.avg_price),
      avg_sq_ft: parseFloat(row.avg_sq_ft),
      count: parseFloat(row.count),
      id: row.id,
      title: row.title,
    }));
const financialBreakdown = FinancialBreakdownController();
export const AssetBreakdown = forwardRef((props, ref) => {
  const { showToast } = useToast();
  const { next } = useAccordionWizard();
  const [errors, setErrors] = useState({});
  const { id: opportunityId } = useParams();
  const [assetBreakdown, handleGetAssetBreakdown] = useFetchState();
  const [updateAssetBreakdown, handleUpdateAssetBreakdown] = useFetchState();
  const [hasParkingStorage, setHasParkingStorage] = useState({});
  const form = useForm({
    defaultValues: getDefaultFormValue(),
  });
  const [assets] = useWatch({
    control: form.control,
    name: ["assets"],
  });

  useEffect(() => {
    handleGetAssetBreakdown(
      financialBreakdown.getAssetBreakdown,
      opportunityId
    );
  }, []); // eslint-disable-line
  useImperativeHandle(ref, () => ({
    refetch() {
      handleGetAssetBreakdown(
        financialBreakdown.getAssetBreakdown,
        opportunityId
      );
    },
  }));
  useEffect(() => {
    if (
      !assetBreakdown.isError &&
      !assetBreakdown.isLoading &&
      assetBreakdown.data
    ) {
      form.reset({
        ...form.getValues(),
        ...assetBreakdown.data,
        assets: processData(assetBreakdown.data.assets),
        parking_assets: PSProcessData(assetBreakdown.data.parking_assets),
        storage_assets: PSProcessData(assetBreakdown.data.storage_assets),
      });
      setHasParkingStorage({
        parking_assets:
          !!assetBreakdown.data.parking_assets &&
          assetBreakdown.data.parking_assets.length > 0,
        storage_assets:
          !!assetBreakdown.data.storage_assets &&
          assetBreakdown.data.storage_assets.length > 0,
      });
    }
  }, [assetBreakdown]); // eslint-disable-line
  const sectionGroups = chunk(Object.keys(assets), 3);

  const save = (values) => {
    let errors = assetsValidation(values.assets);
    const pErrors = pSValidation(
      "parking_assets",
      values.parking_assets,
      hasParkingStorage["parking_assets"]
    );
    const sErrors = pSValidation(
      "storage_assets",
      values.storage_assets,
      hasParkingStorage["storage_assets"]
    );
    if (pErrors) errors = { ...errors, ...pErrors };
    if (sErrors) errors = { ...errors, ...sErrors };
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      const body = {
        assets: deserializeAssets(values.assets),
        parking_assets: parsePSdata(values.parking_assets),
        storage_assets: parsePSdata(values.storage_assets),
        sale_tax_percentage:
          values.sale_tax_percentage && values.project_purpose === "sale"
            ? parseFloat(values.sale_tax_percentage)
            : undefined,
        id: opportunityId,
      };

      if (!hasParkingStorage["parking_assets"]) delete body.parking_assets;
      if (!hasParkingStorage["storage_assets"]) delete body.storage_assets;

      handleUpdateAssetBreakdown(financialBreakdown.updateAssetBreakdown, body);
    }
  };

  useEffect(() => {
    if (
      !updateAssetBreakdown.isError &&
      !updateAssetBreakdown.isLoading &&
      updateAssetBreakdown.data === ""
    ) {
      showToast("Asset breakdown updated successfuly", "success");
      next();
      if (props.onSave) props.onSave();
    }
  }, [updateAssetBreakdown]); // eslint-disable-line

  return (
    <div className={"mt-4"} ref={ref}>
      <FormProvider {...form}>
        <AccordionCard
          headerText="Asset Breakdown"
          accordionKey="asset-breakdown"
        >
          {assetBreakdown.data && (
            <form onSubmit={form.handleSubmit(save)}>
              {sectionGroups.map((groupedKeys, i) => {
                return (
                  <AssetBreakdownSection
                    key={i + 1}
                    groupedKeys={groupedKeys}
                    prefixKey="assets"
                    errors={errors}
                  />
                );
              })}

              <AssetBreakdownSection
                groupedKeys={["parking_assets", "storage_assets"]}
                titleParser={(key) =>
                  ({
                    parking_assets: "Parking",
                    storage_assets: "Storage",
                  }[key])
                }
                errors={errors}
                conditionalTable
                conditionLabel={(key) =>
                  ({
                    parking_assets: "Are there parking spots available?",
                    storage_assets: "Are there storage spots available?",
                  }[key])
                }
                conditionValues={hasParkingStorage}
                onConditionChange={setHasParkingStorage}
              />
              <SummerySection hasParkingStorage={hasParkingStorage} />

              <Row className={"mt-5"}>
                <Col />
                <Col xs={2}>
                  <Button
                    label={"Save and Next"}
                    loading={updateAssetBreakdown.isLoading}
                  />
                </Col>
              </Row>
            </form>
          )}
        </AccordionCard>
      </FormProvider>
    </div>
  );
});
